import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as api from "../../redux/api";
import { Country } from "country-state-city";
import Loader from "../../components/loaders/Loader";
import { reactIcons } from "../../utils/icons";
import { useNavigate, useParams } from "react-router-dom";

import EntityFormVault from "../../components/Forms/EntityFormVault";
import CustomFileInput from "../../components/CustomFileInput";
import BackButton from "../../components/BackButton";

const initialState = {
  type: "",
  name: "",
  RegistrationNumber: "",
  emailAddress: "",
  phoneNumber: "",
  website: "",
  address: "",
  dateOfIncorporation: null,
  vaultFacilityDescription: "",
  efficiencyMetrics: "",
  ol: [],
  ecl: [],
  vaultSecurityMeasures: [],
  vaultInsuranceType: [],
  vaultStorageCapacity: ""
};

const AddVaults = () => {
  const [step1, setStep1] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(initialState);
  const [formError, setFormError] = useState({});
  const [dialCode, setDialCode] = useState("91");
  const [selectedCountry, setSelectedCountry] = useState("IN");
  const [countryName, setCountryName] = useState("India");
  const [isReadOnly, setIsReadOnly] = useState(false);

  const navigate = useNavigate();

  const { id } = useParams();
  const [apiData, setApiData] = useState({});

  const securityOptions = [
    "24/7 surveillance",
    "biometric controls",
    "armed guards",
    "other"
  ];
  const insuranceOptions = [
    "liability insurance",
    "theft/loss coverage",
    "other"
  ];
  const getData = async () => {
    try {
      if (!id) return;

      const res = await api.getUserList(id);
      if (res?.status && res?.data?.data) {
        const userData = res.data.data;
        setApiData(res.data);
        const orgRole = userData.OrganizationMeta?.[0] || {};
        const baseUrl = "https://ventursa-api.codesfortomorrow.com/storage";
        setIsReadOnly(true);

        const liabilityDocs = (
          orgRole.vaultLiabilityInsuranceDocument || []
        ).map((filename) => ({
          url: `${baseUrl}/${filename}`,
          name: filename
        }));
        const auditReports = (orgRole.vaultSecurityAuditReport || []).map(
          (filename) => ({ url: `${baseUrl}/${filename}`, name: filename })
        );

        setForm((prev) => ({
          ...prev,
          vaultFacilityDescription: orgRole.vaultFacilityDescription || "",
          vaultStorageCapacity: orgRole.vaultStorageCapacity || "",
          vaultSecurityMeasures: orgRole.vaultSecurityMeasures || "",
          vaultInsuranceType: orgRole.vaultInsuranceType || "",
          ol: liabilityDocs,
          ecl: auditReports
        }));
      }
    } catch (error) {
      console.error("API Error:", error?.message || error);
    }
  };

  useEffect(() => {
    if (id) {
      getData();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value
    });
    setFormError({
      ...formError,
      [name]: ""
    });
  };

  const handleStepComplete = (stepData) => {
    setForm({
      ...form,
      ...stepData
    });
    setStep1(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    let errors = {};

    if (!form.vaultFacilityDescription.trim()) {
      errors.vaultFacilityDescription = "Facility description is required";
    }

    if (!form.vaultSecurityMeasures?.length) {
      errors.vaultSecurityMeasures = "Select at least one security measure";
    }

    if (!form.vaultInsuranceType?.length) {
      errors.vaultInsuranceType = "Select at least one insurance type";
    }

    if (!form.ol?.length) {
      errors.ol = "Please upload at least one liability insurance document";
    }

    if (!form.ecl?.length) {
      errors.ecl = "Please upload at least one security audit report";
    }

    if (Object.keys(errors).length) {
      return setFormError(errors);
    }

    try {
      setIsLoading(true);

      const payload = {
        type: form.type,
        primaryContactName: form.primaryContactName,
        name: form.name,
        registrationNumber: form.RegistrationNumber,
        emailAddress: form.emailAddress,
        phoneNumber: `+${dialCode}${form.phoneNumber}`,
        website: form.website || "",
        address: form.address,
        dateOfIncorporation: form.dateOfIncorporation,
        dialCode: "+" + dialCode,
        country: countryName,
        countryOfIncorporation: form.countryOfIncorporation,
        vaultFacilityDescription: form.vaultFacilityDescription,
        efficiencyMetrics: form.efficiencyMetrics || "",
        documentationChecklist: {},
        beneficialOwners: [],
        complianceRiskAssessment: [],

        addedBy: "admin",
        organizationMeta: [
          {
            vaultFacilityDescription: form?.vaultFacilityDescription,
            vaultStorageCapacity: form?.vaultStorageCapacity,
            vaultInsuranceType: form?.vaultInsuranceType.join(","),
            vaultSecurityMeasures: form?.vaultSecurityMeasures,
            vaultLiabilityInsuranceDocument: (form.ol || []).map((f) => f.name),
            vaultSecurityAuditReport: (form.ecl || []).map((f) => f.name)
          }
        ]
      };

      const res = await api.registerUser(payload);
      if (res.status === 200 || res.status === 201) {
        toast.success("Vault Added Successfully");
        navigate(`/documentation-checklist/${res.data.id}`, {
          state: { key: "saurabh" }
        });
        localStorage.setItem("organizationId", res.data.organizationId);
      } else {
        toast.error(res?.error?.message);
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        Array.isArray(error.response.data.message)
      ) {
        toast.error(error.response.data.message[0]);
      } else if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      } else {
        toast.error("An error occurred while submitting the form");
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    setSelectedCountry(countryCode);
    const country = Country.getAllCountries().find(
      (c) => c.isoCode === countryCode
    );
    setCountryName(country.name);
    setDialCode(country?.phonecode);
  };

  useEffect(() => {
    const country = Country.getAllCountries().find((c) => c.isoCode === "IN");
    if (country) {
      setCountryName(country.name);
    }
  }, []);
  const handleSecuritySelect = (measure) => {
    setForm((prev) => {
      const selected = prev.vaultSecurityMeasures.includes(measure);
      const updated = selected
        ? prev.vaultSecurityMeasures.filter((m) => m !== measure)
        : [...prev.vaultSecurityMeasures, measure];

      return {
        ...prev,
        vaultSecurityMeasures: updated
      };
    });

    setFormError((prev) => ({
      ...prev,
      vaultSecurityMeasures: ""
    }));
  };

  const handleInsuranceSelect = (type) => {
    setForm((prev) => {
      const selected = prev.vaultInsuranceType.includes(type);
      const updated = selected
        ? prev.vaultInsuranceType.filter((t) => t !== type)
        : [...prev.vaultInsuranceType, type];

      return {
        ...prev,
        vaultInsuranceType: updated
      };
    });

    setFormError((prev) => ({
      ...prev,
      vaultInsuranceType: ""
    }));
  };

  return (
    <div>
      <header className="flex items-center gap-2">
        <BackButton />
        <h4 className="">Add Vault</h4>
      </header>
      <div className="border mt-5 border-yellow-dark p-5 rounded-[10px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (id) {
              navigate(`/documentation-checklist/${id}`);
            } else {
              handleSubmit(e);
            }
          }}
        >
          <div className="relative">
            {!step1 ? (
              <div className="font-semibold text-black text-20">
                Organization Information
              </div>
            ) : (
              <div className="font-semibold text-black text-20">Vaults</div>
            )}
            {!step1 ? (
              <EntityFormVault
                handleChange={handleChange}
                formError={formError}
                handleCountryChange={handleCountryChange}
                selectedCountry={selectedCountry}
                Country={Country}
                dialCode={dialCode}
                onStepComplete={handleStepComplete}
                form={apiData?.data}
                isReadOnly={isReadOnly}
              />
            ) : (
              <div className="lg:grid grid-cols-1 sm:grid-cols-2 gap-6 items-start mt-4">
                <div className="mb-3 lg:mb-0">
                  <label className="mb-2 block  text-black">
                    Facility description<span className="text-red-600">*</span>
                  </label>
                  <textarea
                    type="text"
                    placeholder="Enter Facility description"
                    autoComplete="off"
                    name="vaultFacilityDescription"
                    onChange={(e) => {
                      handleChange(e);
                      setFormError((f) => ({
                        ...f,
                        vaultFacilityDescription: ""
                      }));
                    }}
                    value={form.vaultFacilityDescription}
                    required
                    readOnly={isReadOnly}
                    className={`text-input ${
                      isReadOnly ? "readonly-input" : ""
                    }`}
                  />
                  {formError.vaultFacilityDescription && (
                    <div className="form-eror">
                      {formError.vaultFacilityDescription}
                    </div>
                  )}
                </div>
                <div className="mb-3 lg:mb-0">
                  <label className="mb-2 block  text-black">
                    Storage capacity
                  </label>
                  <input
                    type="text"
                    placeholder="Enter  Storage capacity"
                    autoComplete="off"
                    name="vaultStorageCapacity"
                    onChange={handleChange}
                    // className="text-input text-black text-12 lg:text-14 border"
                    value={form.vaultStorageCapacity}
                    readOnly={isReadOnly}
                    className={`text-input ${
                      isReadOnly ? "readonly-input" : ""
                    }`}
                  />
                  {formError.vaultStorageCapacity && (
                    <div className="form-eror">
                      {formError.vaultStorageCapacity}
                    </div>
                  )}
                </div>
                <div className="mb-3 lg:mb-0">
                  <label className="mb-2 block  text-black">
                    Security measures<span className="text-red-600">*</span>
                  </label>

                  <div className="flex gap-3 flex-wrap">
                    {securityOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => handleSecuritySelect(opt)}
                        disabled={isReadOnly}
                        className={`px-4 py-2 rounded-md border
      ${
        form.vaultSecurityMeasures.includes(opt)
          ? "bg-yellow-700 text-white border-yellow-700"
          : "bg-white text-black border-gray-300"
      }
      ${isReadOnly ? "opacity-50 cursor-not-allowed" : "hover:bg-yellow-100 text-black"}`}
                      >
                        {opt}
                      </button>
                    ))}
                    {formError.vaultSecurityMeasures && (
                      <div className="form-eror">
                        {formError.vaultSecurityMeasures}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mb-3 lg:mb-0">
                  <label className="mb-2 block  text-black">
                    Insurance type
                  </label>

                  <div className="flex gap-3 flex-wrap">
                    {insuranceOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => handleInsuranceSelect(opt)}
                        disabled={isReadOnly}
                        className={`px-4 py-2 rounded-md border
      ${
        form.vaultInsuranceType.includes(opt)
          ? "bg-yellow-700 text-white border-yellow-700"
          : "bg-white text-black border-gray-300"
      }
      ${isReadOnly ? "opacity-50 cursor-not-allowed" : "hover:bg-yellow-100 text-black"}`}
                      >
                        {opt}
                      </button>
                    ))}
                    {formError.vaultInsuranceType && (
                      <div className="form-eror">
                        {formError.vaultInsuranceType}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <CustomFileInput
                    label="Liability Insurance Document"
                    allowedFormats="pdf"
                    name="vaultLiabilityInsuranceDocument"
                    defaultValue={form.ol.map((f) => f.url)}
                    onChange={(urls) => {
                      const docs = urls.map((url) => ({
                        url,
                        name: url.split("/").pop()
                      }));
                      setForm((prev) => ({ ...prev, ol: docs }));
                      setFormError((f) => ({ ...f, ol: "" }));
                    }}
                    disabled={isReadOnly}
                  />
                  {formError.ol && (
                    <div className="form-eror">{formError.ol}</div>
                  )}
                </div>
                <div>
                  <CustomFileInput
                    label="Security Audit Report"
                    allowedFormats="pdf"
                    name="vaultSecurityAuditReport"
                    defaultValue={form.ecl.map((f) => f.url)}
                    onChange={(urls) => {
                      const docs = urls.map((url) => ({
                        url,
                        name: url.split("/").pop()
                      }));
                      setForm((prev) => ({ ...prev, ecl: docs }));
                      setFormError((f) => ({ ...f, ecl: "" }));
                    }}
                    disabled={isReadOnly}
                  />
                  {formError.ecl && (
                    <div className="form-eror">{formError.ecl}</div>
                  )}
                </div>
              </div>
            )}
          </div>
          {!step1 ? (
            <div className="flex gap-2 mt-5 justify-end items-center">
              {/* Step 2 button is now inside the EntityForm component */}
            </div>
          ) : (
            <div className="flex gap-2 mt-5 justify-end items-center">
              <button
                type="button"
                onClick={() => setStep1(false)}
                className={`btn btn-danger `}
              >
                {reactIcons.arrowleft} Back
              </button>
              <button
                type="submit"
                // disabled={isLoading || !form.vaultFacilityDescription}
                className={`btn btn-primary `}
              >
                {isLoading ? <Loader /> : "Next"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddVaults;
